import { getStore } from "../../../store/createStore";
import subscriptionSelectors from "../../../store/subscription/selectors";
import subscriptionItemSelectors from "../../../store/subscription-item/selectors";
import i18nSelectors from "../../../store/i18n/selectors";
import { getPlanForId } from "../../../utils/planToProduct";
import Subscription from "../../../store/subscription/model";
import SubscriptionItem from "../../../store/subscription-item/model";
import CartProduct from "../../../store/cart-product/model";
import fetchInternal from "../../../utils/fetch";

export const getExpandableSubscriptions = (
  subscriptions: Subscription[],
  activeCartProducts: CartProduct[],
) => {
  return subscriptions.filter((sub) => {
    // Exclude subscriptions that are paid with truemed
    const paymentSource = subscriptionSelectors.paymentSourceForSubscription(
      getStore().getState(),
      sub,
    );
    if (paymentSource?.tokenizationMethod === "truemed") return false;

    // Exclude subscriptions that would exceed the max order volume if the cart is added to it
    const items: SubscriptionItem[] =
      subscriptionItemSelectors.allItemsForSubscription(
        getStore().getState(),
        sub,
      );
    const existingItemsCount = items.reduce(
      (count, item) => count + item.quantity,
      0,
    );
    const cartItemsCount = activeCartProducts.reduce(
      (count, product) => count + product.quantity,
      0,
    );
    const maxOrderItems = i18nSelectors.maxOrderItems(getStore().getState());
    const totalItems = existingItemsCount + cartItemsCount;
    if (totalItems > maxOrderItems) return false;

    // Exclude subscriptions that have mixed cadence, or mixed cadence is in the cart
    const itemPlans = items.map((item) => getPlanForId(item.planId));
    const cartProductPlans = activeCartProducts.map((cartProduct) =>
      getPlanForId(cartProduct.planId),
    );

    const intervalCounts = itemPlans
      .concat(cartProductPlans)
      .map((plan) => plan?.intervalCount);

    // each cart product and sub product need to either match the same numeric intervalCount or be null (one time purchase)
    return intervalCounts.every(
      (ic) => ic === intervalCounts.filter(Boolean)[0] || ic === null,
    );
  });
};

export const addCartToSubscription = async (
  subscriptionId: string,
  promotionId?: string,
) => {
  const body: { promotion_id?: string } = {};
  if (promotionId) {
    body.promotion_id = promotionId;
  }

  const response = await fetchInternal(
    `/subscriptions/${subscriptionId}/add_from_cart`,
    {
      method: "PATCH",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      fetchOptions: {
        addCastleRequestToken: true,
      },
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    },
  );

  return response;
};

interface SubItemAttribute {
  plan_id: string;
  quantity: number;
}

const serializeItems = (
  items: (SubscriptionItem | CartProduct)[],
): SubItemAttribute[] => {
  return items.map((item: SubscriptionItem | CartProduct) => {
    return {
      plan_id: item.planId,
      quantity: item.quantity,
    };
  });
};

const constructSubscriptionItemsAttributes = (
  cartProducts: CartProduct[],
  subscriptionItems: SubscriptionItem[],
) => {
  const serializedCartProducts = serializeItems(cartProducts);
  const serializedSubscriptionItems = serializeItems(subscriptionItems);

  return serializedCartProducts.reduce((mergedItems, cartItem) => {
    const existingItem = mergedItems.find(
      (item) => item.plan_id === cartItem.plan_id,
    );
    if (existingItem) {
      existingItem.quantity += cartItem.quantity;
    } else {
      mergedItems.push(cartItem);
    }
    return mergedItems;
  }, serializedSubscriptionItems);
};

export const fetchSubscriptionInvoicePreview = async (
  cartProducts: CartProduct[],
  subscription?: Subscription,
) => {
  if (!subscription) return;
  const subscriptionItems: SubscriptionItem[] =
    subscriptionItemSelectors.allItemsForSubscription(
      getStore().getState(),
      subscription,
    );

  const subscriptionItemsAttributes = constructSubscriptionItemsAttributes(
    cartProducts,
    subscriptionItems,
  );

  const itemPlans = subscriptionItems.map((item) => getPlanForId(item.planId));
  const firstPlan = itemPlans[0];

  const preview = await fetchInternal(`subscriptions/preview`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    fetchOptions: {
      addCastleRequestToken: true,
    },
    body: JSON.stringify({
      address_id: subscription.addressId,
      subscription_id: subscription.id,
      subscription_items_attributes: subscriptionItemsAttributes,
      interval_count: firstPlan?.intervalCount,
    }),
  });

  return preview.data.attributes;
};
